/**
 * SCM platform catalog (plan 494, S-3): the one list of source-control platform ids.
 *
 * Browser-safe (zod only). Every backend platform list derives from here, so a new provider
 * (Azure DevOps, spec 493) is added once instead of as another arm in every list.
 *
 * `github-app` is not a platform: it is a connection kind that stays on the wire as it is.
 */
import { z } from 'zod';
export const SCM_PLATFORMS = ['github', 'gitlab', 'gitea', 'azure_devops'];
export const scmPlatformSchema = z.enum(SCM_PLATFORMS);
/**
 * Platforms with a provider implementation in `packages/scm` (review, commit status, code fetch,
 * webhooks, branch listing). Azure DevOps joined in spec 493 A-4; every branch over this list is
 * either exhaustive (`assertNeverScmPlatform`) or listed under "Implicit fallbacks" in
 * specs/494-scm-provider-registry/plan.md.
 */
export const INTEGRATED_SCM_PLATFORMS = [
    'github',
    'gitlab',
    'gitea',
    'azure_devops',
];
export function isIntegratedScmPlatform(value) {
    return INTEGRATED_SCM_PLATFORMS.includes(value);
}
export function isScmPlatform(value) {
    return SCM_PLATFORMS.includes(value);
}
export const SCM_PLATFORM_CATALOG = {
    github: {
        label: 'GitHub',
        shortLabel: 'GH',
        iconKey: 'github',
        logo: '/logos/github.svg',
        availability: 'ga',
    },
    gitlab: {
        label: 'GitLab',
        shortLabel: 'GL',
        iconKey: 'gitlab',
        logo: '/logos/gitlab.svg',
        availability: 'flagged',
        featureFlag: 'FEATURE_GITLAB_ENABLED',
    },
    gitea: {
        label: 'Gitea',
        shortLabel: 'Gitea',
        iconKey: 'git-branch',
        logo: '/logos/gitea.svg',
        availability: 'flagged',
        featureFlag: 'FEATURE_GITEA_ENABLED',
    },
    azure_devops: {
        label: 'Azure DevOps',
        shortLabel: 'ADO',
        iconKey: 'infinity',
        logo: '/logos/azure-devops.svg',
        availability: 'flagged',
        featureFlag: 'FEATURE_AZURE_DEVOPS_ENABLED',
        comingSoonWhenFlagOff: true,
    },
};
export const SCM_PLATFORM_LABELS = {
    github: SCM_PLATFORM_CATALOG.github.label,
    gitlab: SCM_PLATFORM_CATALOG.gitlab.label,
    gitea: SCM_PLATFORM_CATALOG.gitea.label,
    azure_devops: SCM_PLATFORM_CATALOG.azure_devops.label,
};
/** Retired spellings still accepted on input (plan 494 "Wire names"); never emitted. */
const LEGACY_WIRE_ALIASES = {
    'azure-devops': 'azure_devops',
};
/** Canonical id for a platform value from an API body, query or path; `null` if unknown. */
export function parseScmPlatformWire(value) {
    if (typeof value !== 'string')
        return null;
    const parsed = scmPlatformSchema.safeParse(value);
    if (parsed.success)
        return parsed.data;
    return LEGACY_WIRE_ALIASES[value] ?? null;
}
/** `scmPlatformSchema` that also accepts the legacy aliases, for request input. */
export const scmPlatformWireSchema = z.preprocess((value) => parseScmPlatformWire(value) ?? value, scmPlatformSchema);
/** Exhaustiveness guard for branches over `ScmPlatform` / `IntegratedScmPlatform`. */
export function assertNeverScmPlatform(value) {
    throw new Error(`Unhandled SCM platform: ${String(value)}`);
}
//# sourceMappingURL=catalog.js.map